import { Box, Button, HStack } from "@chakra-ui/react"
import Link from "@pathwright/ui/src/components/link/Link"
import Pathicon from "@pathwright/ui/src/components/pathicon/Pathicon"
import UserAvatarStack from "@pathwright/ux/src/components/avatar/UserAvatarStack"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { forwardRef } from "react"
import { ShareDumb } from "../../../share/Share"
import ManageMentorGroupCard from "../../manage/ManageMentorGroupCard"
import MentorGroupControls from "./MentorGroupControls"
import { useMentorGroupListContext } from "./MentorGroupListContext"

const GhostButton = forwardRef(({ children, ...props }, ref) => (
  <DarkMode>
    <Button
      ref={ref}
      variant="ghost"
      colorPalette="gray"
      size="sm"
      fontSize="1em"
      p={2}
      pos="relative"
      {...props}
    >
      <HStack gap={1}>{children}</HStack>
    </Button>
  </DarkMode>
))

const MentorGroupControlsContainer = () => {
  const { mentorGroup, groupSubscription, permissions } =
    useMentorGroupListContext()

  return permissions.can_edit_group && permissions.can_edit_subscription ? (
    <MentorGroupControls
      mentorGroup={mentorGroup}
      groupSubscription={groupSubscription}
    >
      {({ indicator }) => (
        // TODO: use buttonProps.indicator? Already showing indicar on MentorGroupSeats for same condition...
        <GhostButton>
          <Pathicon icon="ellipses-horizontal" />
        </GhostButton>
      )}
    </MentorGroupControls>
  ) : (
    <GhostButton asChild borderRadius="full">
      <Link
        relativeTo={{
          pathname: "manage",
          card: {
            component: ManageMentorGroupCard,
            props: {
              id: mentorGroup.id
            }
          }
        }}
      >
        <Pathicon icon="gear" />
      </Link>
    </GhostButton>
  )
}

const MentorGroupAvatars = ({ avatars }) => {
  return avatars.length ? (
    <HStack gap={1} minW={0} p={1}>
      <UserAvatarStack size="2xs" users={avatars} max={3} />
    </HStack>
  ) : null
}

const MentorGroupAdd = forwardRef(({ onToggle, ...rest }, ref) => {
  const { t } = useTranslate()
  const { mentorGroup } = useMentorGroupListContext()

  return (
    <GhostButton ref={ref} {...rest} onClick={onToggle} fontWeight="normal">
      <Pathicon icon="avatar-plus" />{" "}
      {mentorGroup.seat_limit ? (
        <>
          <strong>{t("mentor_group.members.list.add")}</strong>{" "}
          <Box
            as="span"
            opacity={0.5}
          >{`${mentorGroup.seats_filled}/${mentorGroup.seat_limit}`}</Box>
        </>
      ) : (
        <strong>{t("mentor_group.members.list.add")}</strong>
      )}
    </GhostButton>
  )
})

const MentorGroupIsland = () => {
  const { mentorGroup, staff, members } = useMentorGroupListContext()
  const avatars = [...staff, ...members].map((member) => member.user)

  return mentorGroup ? (
    <Box
      bg="{colors.gray.900/90}"
      borderRadius="50px"
      backdropFilter="blur(20px)"
      p={1}
      position="relative"
      zIndex={10}
    >
      <HStack className="chakra-ui-reset-css" display="inline-flex" gap={1}>
        <MentorGroupAvatars avatars={avatars} />
        <ShareDumb
          context={{ type: "group", typeId: mentorGroup.id }}
          openWithKeys
          // Using modal because a popover could be clipped vertically if the content is too tall.
          displayType="modal"
        >
          {({ renderTrigger }) => renderTrigger(MentorGroupAdd)}
        </ShareDumb>
        <MentorGroupControlsContainer />
      </HStack>
    </Box>
  ) : null
}

export default MentorGroupIsland
